/* eslint-disable no-nested-ternary */
import {
  AlertTriangle,
  ArrowNarrowUp,
  ChevronDown,
  ChevronUp,
  Mail,
  Stars,
  Cross,
} from '@saleshandy/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ImageIcon } from '@saleshandy/design-system';

import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import {
  EmailContentSizeAllowed,
  SubjectSizeAllowed,
} from '../../../../shared/utils';
import { Images } from '../../../../shared/app-constants';
import TextEditorSkeletonContainer, {
  TextEditorSkeleton,
} from '../modals/email-modal/components/ai-variant-skeletons';
import { AiVariantSkeletons } from '../modals/email-modal/components/ai-variant-skeletons/constants';
import Input from '../../../../shared/design-system/components/input';
import Editor from '../../../../shared/editor';

interface AIPromptPopoverProps {
  onTriggerAI: (aiContent: string) => void;
  setShowPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  aiContent: string;
  onAiContentChange: (val: string) => void;
  popoverRef: React.MutableRefObject<HTMLDivElement>;
}

const AIPromptPopover: React.FC<AIPromptPopoverProps> = ({
  setShowPrompt,
  aiContent,
  onAiContentChange,
  popoverRef,
  onTriggerAI,
}) => {
  const [touched, setTouched] = useState(false);
  const trimmedLength = useMemo(() => aiContent?.trim()?.length, [aiContent]);
  const hasError = useMemo(() => trimmedLength < 10 || trimmedLength > 500, [
    trimmedLength,
  ]);

  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const errorMessage = useMemo(() => {
    if (!touched) return '';
    if (trimmedLength < 10) return 'Minimum 10 characters required.';
    if (trimmedLength > 500) return 'Maximum 500 characters allowed.';
    return '';
  }, [touched, trimmedLength]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div
      className={`custom-ai-popover ${touched && hasError ? 'error' : ''}`}
      {...accessibleOnClick((e) => e.stopPropagation())}
      ref={popoverRef}
    >
      <textarea
        ref={textareaRef}
        className="ai-textarea"
        placeholder="What would you like to modify this in step ?"
        value={aiContent}
        onChange={(e) => onAiContentChange(e.target.value)}
        onBlur={() => setTouched(true)}
      />

      <div className="popover-footer">
        {errorMessage && <p className="error-message">{errorMessage}</p>}
        <button
          className="send-button"
          disabled={hasError}
          onClick={() => {
            setShowPrompt(false);
            onTriggerAI(aiContent);
          }}
          type="button"
        >
          <ArrowNarrowUp />
        </button>
      </div>
    </div>
  );
};

interface EmailAccordionItemProps {
  id: number;
  title: string;
  subject: string;
  body: string;
  aiContent: string;
  isExpanded: boolean;
  showError?: boolean;
  onExpand: () => void;
  onSubjectChange: (val: string) => void;
  onBodyChange: (val: string) => void;
  onAiContentChange: (val: string) => void;
  isLoading: boolean;
  onTriggerAI: (aiContent: string) => void;
  isDisabled: boolean;
  remainingRegenrationQuota: number;
  onDismissError?: () => void;
  isSubjectReadOnly?: boolean;
}

const EmailAccordionItem: React.FC<EmailAccordionItemProps> = ({
  id,
  title,
  subject,
  body,
  aiContent,
  isExpanded,
  showError,
  onExpand,
  onSubjectChange,
  onBodyChange,
  onAiContentChange,
  onTriggerAI,
  isLoading,
  isDisabled,
  remainingRegenrationQuota,
  onDismissError,
  isSubjectReadOnly,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node)
    ) {
      setShowPrompt(false);
    }
  }, []);

  const handleExpand = useCallback(() => {
    if (!isDisabled) onExpand();
  }, [isDisabled, onExpand]);

  const handleAIIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isExpanded) {
        setShowPrompt((prev) => !prev);
      }
    },
    [isExpanded],
  );

  const handleSubjectChange = useCallback(
    (val: string) => {
      onSubjectChange(val);
    },
    [onSubjectChange],
  );

  const handleBodyChange = useCallback(
    (e) => {
      onBodyChange(e);
    },
    [onBodyChange],
  );

  useEffect(() => {
    if (showPrompt) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPrompt, handleClickOutside]);

  const subjectEmpty = subject?.trim()?.length === 0;
  const subjectTooLong = subject?.length > SubjectSizeAllowed;
  const bodyEmpty = body?.trim()?.length === 0;
  const bodyTooLong = body?.length > EmailContentSizeAllowed;

  const cardClassName = [
    'email-sequence-card',
    isExpanded ? 'expanded' : '',
    showError || bodyEmpty || bodyTooLong ? 'error' : '',
    isExpanded && (showError || bodyEmpty || bodyTooLong)
      ? 'expanded-error'
      : '',
  ].join(' ');

  return (
    <div className="email-sequence-card-wrapper">
      <div className={cardClassName}>
        <div
          className={`card-top-section ${isExpanded ? 'is-expanded' : ''}`}
          {...accessibleOnClick(handleExpand)}
        >
          <div className="day-with-icon">
            <Mail className="icon" />
            <span>{id}</span>
            <div className="divider" />
            <span>Day {title}</span>
          </div>
          <div className="icon-wrap">
            <div className="ai-trigger-wrapper">
              {remainingRegenrationQuota <= 0 ? (
                <div className="disabled-stars">
                  <Stars width={14} height={14} color="#9CA3AF" />
                </div>
              ) : showPrompt ? (
                <div
                  className={`twinkle-icon-wrapper ${
                    showPrompt ? 'no-hover' : ''
                  }`}
                >
                  <ImageIcon
                    src={Images.Stars}
                    width={14}
                    height={14}
                    alt="AI Trigger"
                    onClick={(e) => {
                      if (isExpanded && remainingRegenrationQuota > 0) {
                        handleAIIconClick(e);
                      }
                    }}
                    className="twinkle-icon"
                  />
                </div>
              ) : (
                <div
                  className={`twinkle-icon-wrapper ${
                    showPrompt ? 'no-hover' : ''
                  }`}
                  {...accessibleOnClick((e) => {
                    if (isExpanded && remainingRegenrationQuota > 0) {
                      handleAIIconClick(e);
                    }
                  })}
                >
                  <Stars width={14} height={14} color="#9CA3AF" />
                </div>
              )}

              {showPrompt && (
                <AIPromptPopover
                  popoverRef={popoverRef}
                  aiContent={aiContent}
                  onAiContentChange={onAiContentChange}
                  onTriggerAI={onTriggerAI}
                  setShowPrompt={setShowPrompt}
                />
              )}
            </div>

            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </div>
        </div>

        {isExpanded && (
          <div className="card-body-section">
            <div
              className={`email-modal-input-wrapper${
                subjectEmpty || subjectTooLong ? ' input-with-error' : ''
              }`}
            >
              {isLoading && (
                <div className="ai-generated-subject-loading">
                  <TextEditorSkeleton
                    textEditorSkeletonsList={AiVariantSkeletons.subjectSkeleton}
                  />
                </div>
              )}
              <p className="email-modal-input-label regular-1">Subject</p>
              <div className="email-modal-input-divider" />
              <Input
                className="email-modal-input-box"
                value={subject}
                onChange={handleSubjectChange}
                placeholder="Subject"
                disabled={isSubjectReadOnly}
              />
            </div>
            {(subjectEmpty || subjectTooLong) && (
              <p className="input-error-message">
                {subjectEmpty
                  ? 'Subject cannot be empty.'
                  : `Subject cannot exceed ${SubjectSizeAllowed} characters.`}
              </p>
            )}
            {isLoading ? (
              <div className="ai-generated-body-loading">
                <TextEditorSkeletonContainer />
              </div>
            ) : (
              <div className="text-editor">
                <Editor
                  value={body}
                  onEditorChange={handleBodyChange}
                  showAddAttachments={false}
                />
              </div>
            )}

            {showError && (
              <div className="error-message">
                <div className="error-content">
                  <div className="new-alert">
                    <AlertTriangle width={16} height={16} color="#DC2626" />
                  </div>
                  <div className="description">
                    Sorry, we couldn't process your request at the moment. Your
                    data is untouched. Please try again.
                  </div>
                </div>
                <div className="close-icon">
                  <Cross width={12} height={12} onClick={onDismissError} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {(bodyEmpty || bodyTooLong) && (
        <p className="input-error-message">
          {bodyEmpty
            ? 'Body cannot be empty.'
            : `Body cannot exceed ${(
                EmailContentSizeAllowed /
                (1024 * 1000)
              ).toFixed(1)} MB.`}
        </p>
      )}
    </div>
  );
};

export default EmailAccordionItem;
